import { faPlus, faTimesCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { navigateToStep } from 'actions/onlineBooking/actions/OnlineBookingFlowActions';
import {
    createPaymentMethodSessionThunk,
    fetchPaymentMethodsThunk
} from 'actions/paymentMethods/PaymentMethodsActions';
import { AdyenAddCardDropIn } from 'components/AdyenDropIn/AdyenAddCardDropIn';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { TwoColumnsLayout } from 'components/Layout/TwoColumns/TwoColumnsLayout';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import { OnlineBookingHeader } from 'components/OnlineBooking/Header/OnlineBookingHeader';
import { PurchaseCart } from 'components/OnlineBooking/Purchase/Cart/PurchaseCart';
import { PurchaseFooter } from 'components/OnlineBooking/Purchase/Footer/PurchaseFooter';
import { Alert } from 'components/UI/Alert/Alert';
import { Button } from 'components/UI/Button/Button';
import { SelectableCreditCard } from 'components/UI/SelectableCreditCard/SelectableCreditCard';
import { Spinner } from 'components/UI/Spinner/Spinner';
import { useMarketplace } from 'hooks/useMarketplace';
import React from 'react';
import { useSelector } from 'react-redux';
import { getOnlineBookingCustomer } from 'reducers/onlineBooking/OnlineBookingSelectors';
import {
    getAdyenSession,
    getPaymentMethodsFetched,
    getPaymentMethodsLoading,
    getSessionLoading,
    getSortedPaymentMethods
} from 'reducers/paymentMethods/PaymentMethodsSelectors';
import { dispatch } from 'store';
import { OnlineBookingStep } from 'types/OnlineBooking';
import { PaymentMethod } from 'types/PaymentMethod';

// Demo only: cards are the customer's real ones and "Add new card" opens the Adyen drop-in,
// but there is no purchase endpoint yet, so "Pay" charges nothing
const DEMO_PAYMENT_DELAY_MS = 800;

type PurchaseCheckoutView = 'cards_list' | 'add_card';

export const OnlineBookingPurchaseCheckoutStep: React.FC = () => {
    const marketplace = useMarketplace();
    const customer = useSelector(getOnlineBookingCustomer);

    const paymentMethods = useSelector(getSortedPaymentMethods);
    const paymentMethodsFetched = useSelector(getPaymentMethodsFetched);
    const paymentMethodsLoading = useSelector(getPaymentMethodsLoading);
    const adyenSession = useSelector(getAdyenSession);
    const sessionLoading = useSelector(getSessionLoading);

    const [view, setView] = React.useState<PurchaseCheckoutView>('cards_list');
    const [selectedCard, setSelectedCard] = React.useState<PaymentMethod | null>(null);
    const [hasError, setHasError] = React.useState(false);
    const [isPaying, setIsPaying] = React.useState(false);

    React.useEffect(() => {
        if (customer.id) {
            dispatch(fetchPaymentMethodsThunk(customer.id));
        }
    }, [customer.id]);

    React.useEffect(() => {
        if (paymentMethodsFetched && !selectedCard) {
            const primary = paymentMethods.find(card => card.primary && !card.expired);
            if (primary) {
                setSelectedCard(primary);
            }
        }
    }, [paymentMethodsFetched, paymentMethods, selectedCard]);

    React.useEffect(() => {
        if (!isPaying) return;

        const timeout = setTimeout(
            () => dispatch(navigateToStep(OnlineBookingStep.PurchaseSuccess)),
            DEMO_PAYMENT_DELAY_MS
        );

        return () => clearTimeout(timeout);
    }, [isPaying]);

    const handleAddCard = () => {
        if (!customer.id) return;
        setHasError(false);
        setView('add_card');
        dispatch(createPaymentMethodSessionThunk(customer.id));
    };

    const handleCardAdded = (success: boolean) => {
        if (!success || !customer.id) return;

        setView('cards_list');
        dispatch(fetchPaymentMethodsThunk(customer.id));
    };

    const handleSelectCard = (card: PaymentMethod) => {
        setSelectedCard(card);
        setHasError(false);
    };

    const backHandler = () => {
        if (view === 'add_card') {
            setHasError(false);
            setView('cards_list');
            return;
        }

        dispatch(navigateToStep(OnlineBookingStep.Purchase));
    };

    const hasCards = paymentMethods.length > 0;
    const showSpinner =
        (paymentMethodsLoading && !paymentMethodsFetched) ||
        (view === 'add_card' && sessionLoading && !adyenSession);
    const showEmptyState =
        paymentMethodsFetched && !hasCards && view === 'cards_list' && !showSpinner;
    const showCardsList = view === 'cards_list' && hasCards && !showSpinner;
    const showAddCardDropIn = view === 'add_card' && adyenSession && !sessionLoading;
    const canPay = view === 'cards_list' && !!selectedCard && !selectedCard.expired;

    return (
        <TwoColumnsLayout header={<OnlineBookingHeader onBack={backHandler} />}>
            <LeftColumn contentClassName="flex flex-col flex-1">
                <h1 className="h2 underlined w-full mb-6">Payment Info</h1>

                {hasError && (
                    <Alert variant="error" icon={faTimesCircle} className="mb-5">
                        <b>Your card could not be saved.</b> Please try again or use a different
                        card.
                    </Alert>
                )}

                {showSpinner && (
                    <div className="w-full text-center">
                        <Spinner size={50} />
                    </div>
                )}

                {showEmptyState && (
                    <>
                        <p className="text-base mb-6">
                            You don&apos;t have any saved cards yet. Add a card to make future
                            payments faster and easier.
                        </p>

                        <div className="text-left">
                            <Button variant="link" onClick={handleAddCard}>
                                <FontAwesomeIcon icon={faPlus} />
                                <span className="ml-2 underline">Add new card</span>
                            </Button>
                        </div>
                    </>
                )}

                {showAddCardDropIn && adyenSession && (
                    <AdyenAddCardDropIn
                        session={adyenSession}
                        onComplete={handleCardAdded}
                        onError={() => setHasError(true)}
                    />
                )}

                {hasError && view === 'add_card' && (
                    <div className="text-center mt-4">
                        <Button onClick={handleAddCard}>Retry</Button>
                    </div>
                )}

                {showCardsList && (
                    <>
                        {paymentMethods.map(card => (
                            <SelectableCreditCard
                                key={card.id}
                                card={card}
                                selected={selectedCard?.id === card.id}
                                onSelect={handleSelectCard}
                            />
                        ))}

                        <div className="text-left">
                            <Button variant="link" onClick={handleAddCard}>
                                <FontAwesomeIcon icon={faPlus} />
                                <span className="ml-2 underline">Add new card</span>
                            </Button>
                        </div>
                    </>
                )}
            </LeftColumn>

            <RightColumn
                poweredByCuddles
                footer={
                    <PurchaseFooter
                        label="Pay"
                        loading={isPaying}
                        onSubmit={canPay ? () => setIsPaying(true) : undefined}
                    />
                }
            >
                <div className="flex flex-col gap-5">
                    <MarketplaceInfo
                        inverted
                        marketplace={marketplace}
                        className="hide-on-mobile-and-tablet"
                    />

                    <PurchaseCart flat />
                </div>
            </RightColumn>
        </TwoColumnsLayout>
    );
};
